<template>
    <div class="relative">
        <div class="window-drag-zone fixed top-0 inset-x-0 h-6"></div>
        
        <Search v-model:open="searchOpen" />

        <USlideover v-model="sidebarOpen" side="left" class="lg:hidden">
            <div class="flex flex-col flex-1 p-4 space-y-4">
                <div class="flex items-center justify-between">
                    <img class="size-10" src="@/assets/img/logo.png" />
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="sidebarOpen = false" />
                </div>

                <nav class="flex flex-1 flex-col gap-y-6">
                    <UVerticalNavigation :links="navigationLinks" :ui="navigationLinksUi" />
                </nav>
            </div>
        </USlideover>

        <div class="sticky lg:hidden top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0">
            <UButton @click="sidebarOpen = true" size="xl" color="gray" variant="ghost" icon="i-ph-list-bold" class="-m-2.5 p-2.5" />

            <div class="flex flex-1 items-center self-stretch">
                <!-- <div @click="searchOpen = true" role="button" class="group h-full flex-1 flex items-center space-x-3 px-8">
                    <UIcon name="i-ph-magnifying-glass-duotone" class="size-5 text-gray-400 group-hover:text-gray-500" />
                    <span class="text-gray-500 group-hover:text-gray-600">Search</span>
                </div> -->

                <div class="flex items-center gap-x-4 px-4 lg:gap-x-6 lg:px-8">
                    <UserMenu />
                </div>
            </div>
        </div>

        <div v-if="!isViewer" class="dark hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-80 lg:flex-col">
            <div id="sidebar" class="flex grow flex-col overflow-y-auto bg-gray-900">
                <div v-if="isDesktopApp" class="flex items-center justify-end px-4">
                    <UButton 
                        @click="onBack()"
                        :ui="{ rounded: 'rounded-full' }"
                        icon="i-ph-arrow-left-bold"
                        size="sm" 
                        color="gray" 
                        variant="ghost"
                    />
                    <UButton 
                        @click="onForward()"
                        :ui="{ rounded: 'rounded-full' }"
                        icon="i-ph-arrow-right-bold"
                        size="sm" 
                        color="gray" 
                        variant="ghost"
                    />
                    <UButton 
                        @click="onReload()"
                        :ui="{ rounded: 'rounded-full' }"
                        :loading="reloading"
                        icon="i-ph-arrow-clockwise-bold"
                        loading-icon="i-ph-arrow-clockwise-bold"
                        size="sm" 
                        color="gray" 
                        variant="ghost"
                        class="mx-1"
                    />
                </div>
                <div class="flex flex-col gap-y-5 overflow-y-auto" :class="{ 'mt-4': !isDesktopApp }">
                    <div class="flex pb-1 px-4 shrink-0 items-center justify-between">
                        <img class="size-14" src="@/assets/img/logo.png" />
                        <UserMenu />
                    </div>
                    <nav class="px-4 flex flex-1 flex-col gap-y-6">
                        <UVerticalNavigation :links="navigationLinks" :ui="navigationLinksUi" />
                    </nav>
                </div>
                <div class="sticky bottom-0 px-3 pb-3 mt-auto">
                    <CallCenter v-if="auth.hasRole(UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator)" />
                </div>
            </div>
        </div>

        <div class="relative bg-gray-900 w-full h-screen p-3 overflow-hidden" :class="{ 'lg:pl-80': !isViewer }">
            <main class="h-full overflow-auto p-4 sm:p-6 lg:px-8 bg-gray-50 rounded-lg rounded-r-md">
                <slot />
            </main>
            <div v-if="isViewer" class="absolute inset-x-10 bottom-10 flex items-center space-x-4">
                <button @click="onLogout()" class="group relative size-16 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white">
                    <div class="peer absolute hidden group-hover:flex group-focus:flex items-center justify-center inset-0 rounded-full bg-primary text-white">
                        <UIcon name="i-ph-sign-out-duotone" class="h-7 w-7" aria-hidden="true" />
                    </div>
                    <img class="group-hover:hidden group-focus:hidden h-full w-auto" src="@/assets/img/logo.png" alt="SAMU Logo" />
                </button>
                <div class="flex flex-col">
                    <p class="text-lg text-gray-600 font-medium leading-7 tracking-tight">{{ now.format("dddd, MMMM D, YYYY") }}</p>
                    <p class="text-2xl text-gray-800 font-semibold leading-7 tracking-tight">{{ now.format("hh:mm A") }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import CallCenter from '~/components/call/CallCenter.vue'
import { UserRole } from '~/types/user'
import { FIELD_POST_READ_ROLES } from '~/types/fieldPost'
import { app, platform } from '@todesktop/client-core'

const auth = useAuthStore()

const sidebarOpen = ref(false)
const searchOpen = ref(false)
const reloading = ref(false)
const now = ref<dayjs.Dayjs>(dayjs())
const timeInterval = ref<NodeJS.Timeout>()

const isDesktopApp = computed(() => platform.todesktop.isDesktopApp())
const isViewer = computed(() => auth.user?.role === UserRole.viewer)
const isPartner = computed(() => auth.user?.role === UserRole.partner)
const { overview: partnerOverview, load: loadPartnerOverview } = usePartnerOverview()

// Reset on every account change so one partner never sees another's cached scope.
watch(() => auth.user?.id, () => {
    partnerOverview.value = null
    if (isPartner.value) {
        loadPartnerOverview().catch(() => {})
    }
}, { immediate: true })

const telemedicineNav = useTelemedicineNav()

const navigationLinks = computed(() => {
    if (auth.hasRole(UserRole.telemedicineAdmin, UserRole.telemedicineDoctor)) {
        return telemedicineNav.value.map(link => ({ ...link, click: () => sidebarOpen.value = false }))
    }

    return [
        { label: 'Dashboard', to: '/', icon: 'i-ph-squares-four-duotone', hidden: isPartner.value || auth.hasRole(UserRole.fieldMedic) },
        { label: 'Activations', to: '/activations', icon: 'i-ph-shield-check-duotone', hidden: !isPartner.value },
        { label: 'Distributors', to: '/distributors', icon: 'i-ph-storefront-duotone', hidden: !partnerOverview.value?.canManageDistributors },
        { label: 'Users', to: '/users', icon: 'i-ph-users-duotone', hidden: !partnerOverview.value?.canManageDistributors },
        { label: 'Calls', to: '/calls', icon: 'i-ph-phone-disconnect-duotone', hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator) },
        { label: 'Customers', to: '/customers', icon: 'i-ph-users-three-duotone', hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator) },
        { label: 'Credentials', to: '/credentials', icon: 'i-ph-password-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Coupons', to: '/coupons', icon: 'i-ph-ticket-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Payments', to: '/payments', icon: 'i-ph-credit-card-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Partners', to: '/partners', icon: 'i-ph-handshake-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Transports', to: '/transports', icon: 'i-ph-ambulance-duotone', hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator) },
        { label: 'Field Posts', to: '/field-posts', icon: 'i-ph-first-aid-duotone', hidden: !auth.isEnabled('field_posts') || !auth.hasRole(...FIELD_POST_READ_ROLES) },
        { label: 'Telemedicine', to: '/telemedicine', icon: 'i-ph-video-camera-duotone', hidden: !auth.isEnabled('telemedicine') || !auth.hasRole(UserRole.admin) },
        { label: 'Invoices', to: '/invoices', icon: 'i-ph-receipt-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Broadcasts', to: '/broadcasts', icon: 'i-ph-megaphone-duotone', hidden: !auth.hasRole(UserRole.admin) },
        { label: 'Reports', to: '/reports', icon: 'i-ph-chart-bar-duotone', hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector) },
        { label: 'Settings', to: auth.hasRole(UserRole.medicalDirector) ? '/settings/options' : '/settings', icon: 'i-ph-gear-six-duotone', hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector) },
    ].filter(link => !link.hidden).map(link => {
        return {
            ...link,
            click: () => sidebarOpen.value = false
        }
    })
})

const navigationLinksUi = {
    wrapper: 'space-y-0.5',
    base: 'gap-2.5',
    padding: 'p-2.5',
    label: 'text-sm leading-6 font-semibold',
    icon: {
        base: 'h-6 w-6',
    },
}

onMounted(() => {
    if (isViewer.value) {
        timeInterval.value = setInterval(() => {
            now.value = dayjs()
        }, 10000)
    }
})

onUnmounted(() => {
    if (isViewer.value && timeInterval.value) {
        clearInterval(timeInterval.value)
    }
})

function onReload() {
    reloading.value = true
    window.location.reload()
}

function onBack() {
    useRouter().back()
}

function onForward() {
    useRouter().forward()
}

async function onLogout() {
    await auth.logout()
    await app.quit()
    navigateTo('/login')
}
</script>
